import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

// Card component (simplified version of your Card component)
const Card = ({ value, suit, small = false }) => {
  const getSuitSymbol = (suit) => {
    switch(suit) {
      case 'hearts': return '♥';
      case 'diamonds': return '♦';
      case 'clubs': return '♣';
      case 'spades': return '♠';
      default: return suit;
    }
  };

  const getSuitColor = (suit) => {
    return suit === 'hearts' || suit === 'diamonds' ? '#EF4444' : '#1F2937';
  };

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      style={{
        width: small ? '60px' : '80px',
        height: small ? '84px' : '112px',
        background: 'white',
        borderRadius: '8px',
        border: '2px solid #E5E7EB',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        color: getSuitColor(suit),
        fontWeight: 'bold',
        fontSize: small ? '14px' : '16px'
      }}
    >
      <div>{value}</div>
      <div style={{ fontSize: small ? '18px' : '24px' }}>{getSuitSymbol(suit)}</div>
    </motion.div>
  );
};

const ScoreDisplay = ({ team1Score, team2Score, team1Cards, team2Cards }) => (
  <div style={{
    display: 'flex',
    gap: '20px',
    background: 'rgba(17, 24, 39, 0.8)',
    padding: '16px 24px',
    borderRadius: '12px',
    color: 'white',
    fontWeight: '600'
  }}>
    <div>
      <div style={{ color: '#10B981' }}>Team 1: {team1Score}</div>
      <div style={{ fontSize: '12px', color: '#9CA3AF' }}>Cards: {team1Cards}</div>
    </div>
    <div>
      <div style={{ color: '#F59E0B' }}>Team 2: {team2Score}</div>
      <div style={{ fontSize: '12px', color: '#9CA3AF' }}>Cards: {team2Cards}</div>
    </div>
  </div>
);

const TutorialStep = ({ title, children, onNext, onPrev, isLast, isFirst }) => (
  <div style={{
    background: 'rgba(17, 24, 39, 0.9)',
    borderRadius: '16px',
    padding: '24px',
    color: 'white',
    maxWidth: '800px',
    margin: '0 auto'
  }}>
    <h2 style={{ 
      color: '#10B981', 
      marginBottom: '20px',
      fontSize: '24px',
      fontWeight: 'bold'
    }}>
      {title}
    </h2>
    
    <div style={{ marginBottom: '24px' }}>
      {children}
    </div>
    
    <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between' }}>
      <button
        onClick={onPrev}
        disabled={isFirst}
        style={{
          padding: '12px 24px',
          borderRadius: '8px',
          border: 'none',
          background: isFirst ? '#374151' : '#6B7280',
          color: 'white',
          cursor: isFirst ? 'not-allowed' : 'pointer',
          fontWeight: '600'
        }}
      >
        Previous
      </button>
      
      <button
        onClick={onNext}
        style={{
          padding: '12px 24px',
          borderRadius: '8px',
          border: 'none',
          background: isLast ? '#10B981' : '#3B82F6',
          color: 'white',
          cursor: 'pointer',
          fontWeight: '600'
        }}
      >
        {isLast ? 'Finish Tutorial' : 'Next'}
      </button>
    </div>
  </div>
);

const ZNKTutorial = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [demoState, setDemoState] = useState({
    middle: [],
    team1Score: 0,
    team2Score: 0,
    team1Cards: 0,
    team2Cards: 0
  });

  const resetDemo = () => {
    setDemoState({
      middle: [],
      team1Score: 0,
      team2Score: 0,
      team1Cards: 0,
      team2Cards: 0
    });
  };

  const steps = [
    {
      title: "Welcome to ZNK!",
      content: (
        <div>
          <p style={{ fontSize: '18px', lineHeight: '1.6', marginBottom: '16px' }}>
            ZNK is a trick-taking card game played with 4 players in 2 teams.
          </p>
          <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#E5E7EB' }}>
            Let's learn the scoring rules that make this game exciting!
          </p>
        </div>
      )
    },
    {
      title: "Rule 1: ZNK (Same Rank Cards)",
      content: (
        <div>
          <p style={{ fontSize: '16px', marginBottom: '20px' }}>
            When two cards of the same rank are thrown and there are <strong>no cards in the middle</strong>, 
            it's called a "ZNK" and scores <strong>10 points</strong>.
          </p>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '20px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ marginBottom: '8px' }}>Middle (Empty)</div>
              <div style={{
                width: '200px',
                height: '120px',
                border: '2px dashed #6B7280',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6B7280'
              }}>
                No cards
              </div>
            </div>
            
            <div style={{ fontSize: '24px' }}>→</div>
            
            <div style={{ textAlign: 'center' }}>
              <div style={{ marginBottom: '8px' }}>Two 7s thrown</div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Card value="7" suit="hearts" small />
                <Card value="7" suit="spades" small />
              </div>
            </div>
            
            <div style={{ fontSize: '24px' }}>→</div>
            
            <div style={{ 
              background: '#10B981', 
              color: 'white', 
              padding: '16px', 
              borderRadius: '8px',
              fontWeight: 'bold',
              fontSize: '18px'
            }}>
              ZNK!<br />+10 Points
            </div>
          </div>
          
          <div style={{ 
            background: 'rgba(16, 185, 129, 0.1)', 
            border: '1px solid rgba(16, 185, 129, 0.3)', 
            padding: '12px', 
            borderRadius: '8px',
            color: '#10B981'
          }}>
            💡 Key: ZNK only happens when the middle is empty!
          </div>
        </div>
      )
    },
    {
      title: "Rule 2: Taking Cards (Same Rank with Cards in Middle)",
      content: (
        <div>
          <p style={{ fontSize: '16px', marginBottom: '20px' }}>
            When two cards of the same rank are thrown but there are <strong>already cards in the middle</strong>, 
            the team takes all the cards and scores based on their ranks.
          </p>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '20px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ marginBottom: '8px' }}>Middle (Has cards)</div>
              <div style={{ display: 'flex', gap: '4px' }}>
                <Card value="K" suit="clubs" small />
                <Card value="Q" suit="hearts" small />
              </div>
            </div>
            
            <div style={{ fontSize: '24px' }}>→</div>
            
            <div style={{ textAlign: 'center' }}>
              <div style={{ marginBottom: '8px' }}>Two 8s thrown</div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Card value="8" suit="diamonds" small />
                <Card value="8" suit="clubs" small />
              </div>
            </div>
            
            <div style={{ fontSize: '24px' }}>→</div>
            
            <div style={{ 
              background: '#F59E0B', 
              color: 'white', 
              padding: '16px', 
              borderRadius: '8px',
              fontWeight: 'bold',
              fontSize: '14px'
            }}>
              Team takes<br />all 4 cards!<br />Score by ranks
            </div>
          </div>
          
          <div style={{ 
            background: 'rgba(245, 158, 11, 0.1)', 
            border: '1px solid rgba(245, 158, 11, 0.3)', 
            padding: '12px', 
            borderRadius: '8px',
            color: '#F59E0B'
          }}>
            💡 No ZNK when cards are already in the middle - team collects all cards instead!
          </div>
        </div>
      )
    },
    {
      title: "Rule 3: Jack Takes All",
      content: (
        <div>
          <p style={{ fontSize: '16px', marginBottom: '20px' }}>
            When a <strong>Jack (J)</strong> is thrown, it acts like two cards of the same rank - 
            the team takes all cards from the middle.
          </p>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '20px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ marginBottom: '8px' }}>Middle</div>
              <div style={{ display: 'flex', gap: '4px' }}>
                <Card value="9" suit="spades" small />
                <Card value="K" suit="hearts" small />
                <Card value="A" suit="clubs" small />
              </div>
            </div>
            
            <div style={{ fontSize: '24px' }}>→</div>
            
            <div style={{ textAlign: 'center' }}>
              <div style={{ marginBottom: '8px' }}>Jack thrown</div>
              <div style={{ 
                border: '3px solid #10B981',
                borderRadius: '8px',
                padding: '4px'
              }}>
                <Card value="J" suit="diamonds" small />
              </div>
            </div>
            
            <div style={{ fontSize: '24px' }}>→</div>
            
            <div style={{ 
              background: '#10B981', 
              color: 'white', 
              padding: '16px', 
              borderRadius: '8px',
              fontWeight: 'bold',
              fontSize: '14px'
            }}>
              Team takes<br />all 4 cards!<br />Jack power!
            </div>
          </div>
          
          <div style={{ 
            background: 'rgba(16, 185, 129, 0.1)', 
            border: '1px solid rgba(16, 185, 129, 0.3)', 
            padding: '12px', 
            borderRadius: '8px',
            color: '#10B981'
          }}>
            💡 Jacks are powerful - they always take all cards from the middle!
          </div>
        </div>
      )
    },
    {
      title: "Rule 4: Card Values & Scoring",
      content: (
        <div>
          <p style={{ fontSize: '16px', marginBottom: '20px' }}>
            When teams collect cards, they score points based on specific card values:
          </p>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '20px' }}>
            <div style={{ 
              background: 'rgba(16, 185, 129, 0.1)', 
              border: '1px solid rgba(16, 185, 129, 0.3)', 
              padding: '16px', 
              borderRadius: '8px'
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '12px', color: '#10B981' }}>1 Point Each:</div>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <Card value="J" suit="hearts" small />
                <Card value="J" suit="diamonds" small />
                <Card value="J" suit="clubs" small />
                <Card value="J" suit="spades" small />
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Card value="A" suit="hearts" small />
                <Card value="A" suit="diamonds" small />
                <Card value="A" suit="clubs" small />
                <Card value="A" suit="spades" small />
              </div>
            </div>
            
            <div style={{ 
              background: 'rgba(245, 158, 11, 0.1)', 
              border: '1px solid rgba(245, 158, 11, 0.3)', 
              padding: '16px', 
              borderRadius: '8px'
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '12px', color: '#F59E0B' }}>2 Points Each:</div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                  <Card value="10" suit="diamonds" small />
                  <div style={{ fontSize: '12px', marginTop: '4px' }}>10 of Diamonds</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <Card value="2" suit="clubs" small />
                  <div style={{ fontSize: '12px', marginTop: '4px' }}>2 of Clubs</div>
                </div>
              </div>
            </div>
          </div>
          
          <div style={{ 
            background: 'rgba(139, 92, 246, 0.1)', 
            border: '1px solid rgba(139, 92, 246, 0.3)', 
            padding: '12px', 
            borderRadius: '8px',
            color: '#8B5CF6'
          }}>
            💡 All other cards (K, Q, 10, 9, 8, 7, 6, 5, 4, 3) have no point value
          </div>
        </div>
      )
    },
    {
      title: "Rule 5: Bonus for Card Majority",
      content: (
        <div>
          <p style={{ fontSize: '16px', marginBottom: '20px' }}>
            If a team collects more than <strong>26 cards</strong> in total, 
            they get a bonus of <strong>4 points</strong>.
          </p>
          
          <div style={{ display: 'flex', justifyContent: 'center', gap: '40px', marginBottom: '20px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                background: 'rgba(16, 185, 129, 0.1)', 
                border: '2px solid #10B981',
                borderRadius: '12px',
                padding: '20px',
                minWidth: '150px'
              }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#10B981' }}>Team 1</div>
                <div style={{ fontSize: '32px', margin: '10px 0' }}>28</div>
                <div style={{ fontSize: '14px', color: '#E5E7EB' }}>Cards collected</div>
                <div style={{ 
                  background: '#10B981', 
                  color: 'white', 
                  padding: '8px', 
                  borderRadius: '6px',
                  marginTop: '10px',
                  fontWeight: 'bold'
                }}>
                  +4 Bonus!
                </div>
              </div>
            </div>
            
            <div style={{ textAlign: 'center' }}>
              <div style={{ 
                background: 'rgba(107, 114, 128, 0.1)', 
                border: '2px solid #6B7280',
                borderRadius: '12px',
                padding: '20px',
                minWidth: '150px'
              }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#6B7280' }}>Team 2</div>
                <div style={{ fontSize: '32px', margin: '10px 0' }}>24</div>
                <div style={{ fontSize: '14px', color: '#E5E7EB' }}>Cards collected</div>
                <div style={{ 
                  background: '#6B7280', 
                  color: 'white', 
                  padding: '8px', 
                  borderRadius: '6px',
                  marginTop: '10px'
                }}>
                  No bonus
                </div>
              </div>
            </div>
          </div>
          
          <div style={{ 
            background: 'rgba(16, 185, 129, 0.1)', 
            border: '1px solid rgba(16, 185, 129, 0.3)', 
            padding: '12px', 
            borderRadius: '8px',
            color: '#10B981'
          }}>
            💡 There are 52 cards total, so getting 27+ cards means you got the majority!
          </div>
        </div>
      )
    },
    {
      title: "Summary: Complete Scoring System",
      content: (
        <div>
          <p style={{ fontSize: '16px', marginBottom: '20px' }}>
            Now you know all the scoring rules for ZNK:
          </p>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px', marginBottom: '20px' }}>
            <div style={{ 
              background: 'rgba(16, 185, 129, 0.1)', 
              border: '1px solid rgba(16, 185, 129, 0.3)', 
              padding: '16px', 
              borderRadius: '8px'
            }}>
              <div style={{ fontWeight: 'bold', color: '#10B981', marginBottom: '8px' }}>
                🎯 ZNK (Same rank + empty middle) = 10 points
              </div>
            </div>
            
            <div style={{ 
              background: 'rgba(245, 158, 11, 0.1)', 
              border: '1px solid rgba(245, 158, 11, 0.3)', 
              padding: '16px', 
              borderRadius: '8px'
            }}>
              <div style={{ fontWeight: 'bold', color: '#F59E0B', marginBottom: '8px' }}>
                🃏 Same rank + cards in middle = Take all cards
              </div>
            </div>
            
            <div style={{ 
              background: 'rgba(139, 92, 246, 0.1)', 
              border: '1px solid rgba(139, 92, 246, 0.3)', 
              padding: '16px', 
              borderRadius: '8px'
            }}>
              <div style={{ fontWeight: 'bold', color: '#8B5CF6', marginBottom: '8px' }}>
                👑 Jack = Take all cards (always)
              </div>
            </div>
            
            <div style={{ 
              background: 'rgba(239, 68, 68, 0.1)', 
              border: '1px solid rgba(239, 68, 68, 0.3)', 
              padding: '16px', 
              borderRadius: '8px'
            }}>
              <div style={{ fontWeight: 'bold', color: '#EF4444', marginBottom: '8px' }}>
                💎 Card values: J & A = 1pt, 10♦ & 2♣ = 2pts
              </div>
            </div>
            
            <div style={{ 
              background: 'rgba(34, 197, 94, 0.1)', 
              border: '1px solid rgba(34, 197, 94, 0.3)', 
              padding: '16px', 
              borderRadius: '8px'
            }}>
              <div style={{ fontWeight: 'bold', color: '#22C55E', marginBottom: '8px' }}>
                🏆 27+ cards = +4 bonus points
              </div>
            </div>
          </div>
          
          <div style={{ 
            background: 'rgba(16, 185, 129, 0.2)', 
            border: '2px solid #10B981',
            padding: '16px', 
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ fontWeight: 'bold', fontSize: '18px', color: '#10B981' }}>
              You're ready to play ZNK! 🎉
            </div>
            <div style={{ fontSize: '14px', color: '#E5E7EB', marginTop: '8px' }}>
              Remember: Strategy comes from knowing when to play your Jacks and how to set up ZNKs!
            </div>
          </div>
        </div>
      )
    }
  ];

const nextStep = () => {
  if (currentStep < steps.length - 1) {
    setCurrentStep(currentStep + 1);
    resetDemo();
  } else {
    navigate('/home');
  }
};

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      resetDemo();
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #16213e 35%, #0f3460 100%)',
      padding: '20px',
      fontFamily: '"Inter", system-ui, sans-serif'
    }}>
      {/* Animated background particles */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
          radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.1) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.1) 0%, transparent 50%),
          radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.1) 0%, transparent 50%)
        `,
        pointerEvents: 'none',
        zIndex: 0
      }} />

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <h1 style={{ 
            color: '#10B981', 
            fontSize: '48px', 
            fontWeight: 'bold',
            marginBottom: '10px',
            textShadow: '0 0 20px rgba(16, 185, 129, 0.5)'
          }}>
            ZNK Tutorial
          </h1>
          <div style={{ 
            color: '#E5E7EB', 
            fontSize: '16px',
            background: 'rgba(17, 24, 39, 0.6)',
            padding: '8px 16px',
            borderRadius: '20px',
            display: 'inline-block'
          }}>
            Step {currentStep + 1} of {steps.length}
          </div>
        </div>

        {/* Progress Bar */}
        <div style={{
          width: '100%',
          maxWidth: '800px',
          height: '6px',
          background: 'rgba(75, 85, 99, 0.3)',
          borderRadius: '3px',
          margin: '0 auto 30px auto',
          overflow: 'hidden'
        }}>
          <motion.div
            style={{
              height: '100%',
              background: 'linear-gradient(90deg, #10B981 0%, #34D399 100%)',
              borderRadius: '3px',
              boxShadow: '0 0 10px rgba(16, 185, 129, 0.5)'
            }}
            initial={{ width: '0%' }}
            animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>

        {/* Tutorial Step */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
          >
            <TutorialStep
              title={steps[currentStep].title}
              onNext={nextStep}
              onPrev={prevStep}
              isFirst={currentStep === 0}
              isLast={currentStep === steps.length - 1}
            >
              {steps[currentStep].content}
            </TutorialStep>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ZNKTutorial;